import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { NEUTRAL } from "../../theme/tokens";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import type { EmploymentType, NurseInShift } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { getUniqueLocations } from "../../utils/utilFunctions";
import { FilterSection } from "./components/FilterSection";
import { PaginationControls } from "./components/PaginationControls";
import { ScheduleTable } from "./components/ScheduleTable";
import { getInitialDateRange, PAGE_SIZE } from "./constants";
import { useExportToExcel } from "./hooks/useExportToExcel";
import { useScheduleData } from "./hooks/useScheduleData";
import type { ScheduleReportShift, SortField, SortOrder } from "./types";

export const Schedule: FC = () => {
	const theme = useTheme();
	const { chosenDepartment, getNurseById, hospitalData, showErrorSnackbar } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const [sortField, setSortField] = useState<SortField>("startTime");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
	const { from: initialFrom, to: initialTo } = getInitialDateRange();
	const [startHourFilter, setStartHourFilter] = useState<DateTime | null>(null);
	const [endHourFilter, setEndHourFilter] = useState<DateTime | null>(null);
	const [dateFromFilter, setDateFromFilter] = useState<DateTime | null>(initialFrom);
	const [dateToFilter, setDateToFilter] = useState<DateTime | null>(initialTo);
	const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
	const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
	const [selectedContracts, setSelectedContracts] = useState<EmploymentType[]>([]);
	const [chargeFilter, setChargeFilter] = useState<boolean | null>(null);
	const [useActualShifts, setUseActualShifts] = useState<boolean>(false);
	const [shifts, setShifts] = useState<ScheduleReportShift[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const hasInitialFetch = useRef(false);

	const uniqueLocations = useMemo(() => {
		return getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location);
	}, [chosenDepartment]);

	const availableNurses = useMemo(() => {
		return hospitalData.nurses
			.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
			.sort((a, b) => formatPersonName(a).localeCompare(formatPersonName(b)));
	}, [hospitalData.nurses, chosenDepartment.department_id, formatPersonName]);

	const { totalFilteredCount, filteredAndSortedEntries, totalPages, paginatedEntries } = useScheduleData(
		shifts,
		sortField,
		sortOrder,
		currentPage,
		selectedContracts,
		chargeFilter,
		startHourFilter,
		endHourFilter,
	);

	const { exportToExcel } = useExportToExcel();

	useEffect(() => {
		setCurrentPage(1);
	}, [sortField, sortOrder]);

	const fetchSchedule = useCallback(
		async (useActual?: boolean) => {
			if (!dateFromFilter || !dateToFilter) return;

			try {
				setIsLoading(true);
				const params = new URLSearchParams({
					start_date: dateFromFilter.toFormat("yyyy-MM-dd"),
					end_date: dateToFilter.toFormat("yyyy-MM-dd"),
				});

				if (selectedEmployees.length > 0) {
					params.append("nurse_ids", selectedEmployees.join(","));
				}

				if (selectedLocations.length > 0) {
					params.append("locations", selectedLocations.join(","));
				}

				const actualShiftsValue = useActual !== undefined ? useActual : useActualShifts;
				if (actualShiftsValue) {
					params.append("use_actual", "true");
				}

				const response = await axiosInstance.get(`/schedule/${chosenDepartment.department_id}?${params.toString()}`);
				const shiftsData: NurseInShift[] = response.data as NurseInShift[];

				const shiftsWithNames: ScheduleReportShift[] = shiftsData.map((shift) => {
					const nurse = getNurseById(shift.nurseId);
					const fullName = nurse ? formatPersonName(nurse) : "";
					return {
						...shift,
						startTime: shift.startTime,
						endTime: shift.endTime,
						nurseName: fullName,
						role: nurse?.role ?? shift.role,
						employmentType: nurse?.employmentType ?? shift.type,
						agencyName: nurse?.agencyName ?? "",
					};
				});

				setShifts(shiftsWithNames);
			} catch (error) {
				showErrorSnackbar("Failed to load schedule", { action: "failed to fetch schedule" }, error);
			} finally {
				setIsLoading(false);
			}
		},
		[
			dateFromFilter,
			dateToFilter,
			selectedEmployees,
			selectedLocations,
			useActualShifts,
			chosenDepartment.department_id,
			getNurseById,
			showErrorSnackbar,
			formatPersonName,
		],
	);

	useEffect(() => {
		if (!hasInitialFetch.current) {
			hasInitialFetch.current = true;
			fetchSchedule();
		}
	}, [fetchSchedule]);

	const handleSort = (field: SortField): void => {
		const newSortOrder = field === sortField && sortOrder === "asc" ? "desc" : "asc";
		setSortField(field);
		setSortOrder(newSortOrder);
	};

	const handleExportToExcel = (): void => {
		exportToExcel(filteredAndSortedEntries);
	};

	const handleClearFilters = () => {
		const { from, to } = getInitialDateRange();
		setDateFromFilter(from);
		setDateToFilter(to);
		setSelectedEmployees([]);
		setSelectedLocations([]);
		setSelectedContracts([]);
		setChargeFilter(null);
		setStartHourFilter(null);
		setEndHourFilter(null);
		setUseActualShifts(false);
	};

	const handleActualShiftsChange = (value: boolean) => {
		setUseActualShifts(value);
		if (dateFromFilter && dateToFilter) {
			fetchSchedule(value);
		}
	};

	return (
		<Box
			data-testid="schedule-report-root"
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: 1,
			}}
		>
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					backgroundColor: NEUTRAL[50],
					padding: "16px",
					borderRadius: "4px",
				}}
			>
				<Typography
					sx={{
						color: theme.palette.primary.dark,
						fontSize: "1rem",
						fontWeight: "bold",
					}}
				>
					Schedule report - {useActualShifts ? "Actual Shifts" : "Scheduled Shifts"} ({totalFilteredCount})
				</Typography>
			</Box>

			<FilterSection
				startHour={startHourFilter}
				endHour={endHourFilter}
				dateFromFilter={dateFromFilter}
				dateToFilter={dateToFilter}
				selectedEmployees={selectedEmployees}
				selectedLocations={selectedLocations}
				selectedContracts={selectedContracts}
				chargeFilter={chargeFilter}
				useActualShifts={useActualShifts}
				availableNurses={availableNurses}
				uniqueLocations={uniqueLocations}
				isLoading={isLoading}
				onStartHourChange={setStartHourFilter}
				onEndHourChange={setEndHourFilter}
				onDateFromChange={setDateFromFilter}
				onDateToChange={setDateToFilter}
				onEmployeesChange={setSelectedEmployees}
				onLocationsChange={setSelectedLocations}
				onContractsChange={setSelectedContracts}
				onChargeFilterChange={setChargeFilter}
				onActualShiftsChange={handleActualShiftsChange}
				onClearFilters={handleClearFilters}
				onRunQuery={() => fetchSchedule()}
				onExport={handleExportToExcel}
				canExport={filteredAndSortedEntries.length > 0}
			/>

			<ScheduleTable
				entries={paginatedEntries}
				isLoading={isLoading}
				hasShifts={shifts.length > 0}
				sortField={sortField}
				sortOrder={sortOrder}
				currentPage={currentPage}
				pageSize={PAGE_SIZE}
				onSort={handleSort}
			/>

			{totalFilteredCount > 0 && (
				<PaginationControls
					currentPage={currentPage}
					totalPages={totalPages}
					onPrevious={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
					onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
				/>
			)}
		</Box>
	);
};
